









































































































































import Vue from 'vue';
import MStoreFormSelect from '@/components/shared/MStoreFormSelect.vue';
import MStoreTable from '@/components/shared/Table/MStoreTable.vue';
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue';
import MStoreFormCheckbox from '@/components/shared/MStoreFormCheckbox.vue';
import MStoreSpinnerButton from '@/components/shared/MStoreSpinnerButton.vue';
import { CabinetConfigurationModel, CabinetModel, CabinetReferenceModel, ColourVariantType, ConfirmationModalSettings, DeleteConfigurationModel, DocumentTypeModel, StoreConstants } from '@/types';
import { mapActions, mapGetters } from 'vuex';
import { CabinetConfigurationService } from '@/services/CabinetConfigurationService';
import { CabinetConfigurationHelper, confirmationHelper, FormFieldValidation } from '@/mixins';
import { ValidationModel } from '@/types/models/validation';
import { DocumentLinkConfigurationModel } from '@/types/models/configuration/cabinet/DocumentLinkConfigurationModel';
import { ObjectUtils } from '@/utils/objectUtils';
import SnotifyConfiguration from '@/classes/SnotifyConfiguration';
import { BvTableFieldArray } from 'bootstrap-vue';

export default Vue.extend({
  name: 'ConfigureRelatedDocumentLinks',
  mixins: [CabinetConfigurationHelper, confirmationHelper, FormFieldValidation],
  data() {
    return {
      cabinetConfiguration: {} as CabinetConfigurationModel,
      isLoading: true,
      linkToEdit: {} as DocumentLinkConfigurationModel,
      allCabinets: [] as CabinetModel[],
      bidirectional: false,
      isSubmitting: false,
      deletingLinkId: null,
      validationError: '',
      searchQuery: '',
      currentPage: 1
    };
  },
  computed: {
    ...mapGetters(StoreConstants.CabinetConfiguration.ModuleName, [
      StoreConstants.CabinetConfiguration.Getters.CabinetConfigurationChangeId,
      StoreConstants.CabinetConfiguration.Getters.IsLockedByCurrentSessionId
    ]),
    ...mapGetters(StoreConstants.ApplicationDirty.ModuleName, [StoreConstants.ApplicationDirty.Getters.IsApplicationDirty]),
    isViewMode(this: any) {
      return !this[StoreConstants.CabinetConfiguration.Getters.IsLockedByCurrentSessionId];
    },
    canEditOrDeleteLink(this: any) {
      return this.isInEditLinkMode || Boolean(this.deletingLinkId) || this.isViewMode;
    },
    canAddLink(this: any) {
      return this.canEditOrDeleteLink || this.isSubmitting || !this.sourceDocumentTypes.length;
    },
    headerText(this: any) {
      return `Configure Related Documents in the ${(this.sourceCabinet as CabinetModel)?.description} Cabinet`;
    },
    documentTypes(this: any) {
      return (this.allCabinets as CabinetModel[]).flatMap((x) => x.documentTypes);
    },
    tableItems(this: any) {
      return (
        (this.cabinetConfiguration as CabinetConfigurationModel)?.documentLinks?.map((link) => {
          const sourceDocumentType = (this.documentTypes as DocumentTypeModel[]).find((documentType) => documentType.id === link.sourceDocumentTypeId);
          const targetDocumentType = (this.documentTypes as DocumentTypeModel[]).find((documentType) => documentType.id === link.targetDocumentTypeId);
          let targetCabinetText = '';
          if (sourceDocumentType?.cabinetId !== targetDocumentType?.cabinetId) {
            targetCabinetText = ` (${(this.allCabinets as CabinetModel[])?.find((cabinet: CabinetModel) => cabinet.id === targetDocumentType?.cabinetId)?.description})`;
          }

          const getReferenceId = (documentType: DocumentTypeModel | undefined, referenceId: number | null) => {
            return referenceId ? documentType?.references.find((reference) => reference.id === referenceId) : null;
          };

          const sourceReference1 = getReferenceId(sourceDocumentType, link.sourceReference1Id);
          const sourceReference2 = getReferenceId(sourceDocumentType, link.sourceReference2Id);
          const sourceReference3 = getReferenceId(sourceDocumentType, link.sourceReference3Id);
          const targetReference1 = getReferenceId(targetDocumentType, link.targetReference1Id);
          const targetReference2 = getReferenceId(targetDocumentType, link.targetReference2Id);
          const targetReference3 = getReferenceId(targetDocumentType, link.targetReference3Id);

          return {
            id: link.id,
            // tslint:disable-next-line:max-line-length
            linkDescription: `${sourceDocumentType?.description} to ${targetDocumentType?.description}${targetCabinetText}`,
            linkedRef1: Boolean(sourceReference1) ? `${sourceReference1?.referenceDescription} to ${targetReference1?.referenceDescription}` : '',
            linkedRef2: Boolean(sourceReference2) ? `${sourceReference2?.referenceDescription} to ${targetReference2?.referenceDescription}` : '',
            linkedRef3: Boolean(sourceReference3) ? `${sourceReference3?.referenceDescription} to ${targetReference3?.referenceDescription}` : ''
          };
        }) ?? []
      );
    },
    addButtonTooltip(this: any) {
      return this.isViewMode || this.sourceDocumentTypes?.length ? '' : 'This cabinet has no document types to link from';
    },
    isInEditLinkMode(this: any) {
      return !ObjectUtils.isNullOrEmpty(this.linkToEdit);
    },
    isInAddMode(this: any) {
      return !ObjectUtils.isNullOrEmpty(this.linkToEdit) && !(this.linkToEdit as DocumentLinkConfigurationModel).id;
    },
    addOrEditCardHeader(this: any) {
      return this.linkToEdit?.id > 0 ? 'Edit' : 'Add';
    },
    arrowIcon(this: any) {
      return this.bidirectional ? 'arrow-right-arrow-left' : 'arrow-right-long';
    },
    sourceCabinet(this: any) {
      return (this.allCabinets as CabinetModel[])?.find((x) => x.id === this.$route.params.cabinetId);
    },
    sourceDocumentTypes(this: any) {
      return (this.sourceCabinet as CabinetModel)?.documentTypes ?? [];
    },
    sourceDocumentType(this: any) {
      return (this.sourceDocumentTypes as DocumentTypeModel[]).find((x) => x.id === (this.linkToEdit as DocumentLinkConfigurationModel).sourceDocumentTypeId);
    },
    sourceDocumentTypeId: {
      get(this: any) {
        return (this.linkToEdit as DocumentLinkConfigurationModel).sourceDocumentTypeId;
      },
      set(this: any, value: number) {
        const linkToEdit = this.linkToEdit as DocumentLinkConfigurationModel;
        linkToEdit.sourceDocumentTypeId = value;
        linkToEdit.sourceReference1Id = null;
        linkToEdit.sourceReference2Id = null;
        linkToEdit.sourceReference3Id = null;
      }
    },
    targetCabinets(this: any) {
      return this.allCabinets.filter((x: any) => x.documentTypes?.length);
    },
    targetDocumentType(this: any) {
      const targetDocumentTypeId = (this.linkToEdit as DocumentLinkConfigurationModel)?.targetDocumentTypeId;
      return (this.documentTypes as DocumentTypeModel[])?.find((x) => x.id === targetDocumentTypeId);
    },
    targetCabinet(this: any) {
      return (this.allCabinets as CabinetModel[])?.find((x) => x.id === (this.targetDocumentType as DocumentTypeModel)?.cabinetId);
    },
    targetCabinetId: {
      get(this: any) {
        return (this.targetCabinet as CabinetModel)?.id;
      },
      set(this: any, value: any) {
        this.linkToEdit.targetDocumentTypeId = (this.allCabinets as CabinetModel[]).find((x) => x.id === value)?.documentTypes?.[0].id;
        Vue.set(this.linkToEdit, 'targetReference1Id', null);
        Vue.set(this.linkToEdit, 'targetReference2Id', null);
        Vue.set(this.linkToEdit, 'targetReference3Id', null);
      }
    },
    targetDocumentTypes(this: any) {
      return (this.targetCabinet as CabinetModel)?.documentTypes;
    },
    targetDocumentTypeId: {
      get(this: any) {
        return (this.linkToEdit as DocumentLinkConfigurationModel).targetDocumentTypeId;
      },
      set(this: any, value: number) {
        Vue.set(this.linkToEdit, 'targetDocumentTypeId', value);
        Vue.set(this.linkToEdit, 'targetReference1Id', null);
        Vue.set(this.linkToEdit, 'targetReference2Id', null);
        Vue.set(this.linkToEdit, 'targetReference3Id', null);
      }
    },
    sourceReferences(this: any) {
      return [{ id: 0, referenceDescription: 'No link' }, ...(this.sourceDocumentType?.references ?? [])] as CabinetReferenceModel[];
    },
    targetReferences(this: any) {
      return [{ id: 0, referenceDescription: 'No link' }, ...(this.targetDocumentType?.references ?? [])] as CabinetReferenceModel[];
    },
    referenceLinks(this: any) {
      return [
        [this.linkToEdit?.sourceReference1Id ?? 0, this.linkToEdit?.targetReference1Id ?? 0],
        [this.linkToEdit?.sourceReference2Id ?? 0, this.linkToEdit?.targetReference2Id ?? 0],
        [this.linkToEdit?.sourceReference3Id ?? 0, this.linkToEdit?.targetReference3Id ?? 0]
      ];
    }
  },
  methods: {
    ...mapActions(StoreConstants.CabinetConfiguration.ModuleName, [StoreConstants.CabinetConfiguration.Actions.GetCabinetConfigurationSettingsWithErrorPage]),
    ...mapActions(StoreConstants.Configuration.ModuleName, [StoreConstants.Configuration.Actions.GetConfigurationTree]),
    ...mapActions(StoreConstants.ApplicationDirty.ModuleName, [StoreConstants.ApplicationDirty.Actions.SetApplicationDirty]),
    async getCabinetConfigurationAsync(this: any) {
      try {
        this.cabinetConfiguration = await CabinetConfigurationService.getCabinetConfigurationWithErrorPageAsync(this.$route.params.cabinetId);
        this.cabinetConfiguration.configurationChangeId = this.cabinetConfigurationChangeId;
      } catch (error) {
        this.$log(error);
      }
    },
    selectedRowClass(item: any, type: any): string | undefined {
      return type === 'row' && item?.id === (this.linkToEdit as DocumentLinkConfigurationModel)?.id ? 'table-primary' : '';
    },
    async deleteLinkAsync(this: any, linkId: number) {
      if (!(await this.hasDeleteConfirmedAsync(this.$bvModal))) {
        return;
      }
      this.deletingLinkId = linkId;
      try {
        await CabinetConfigurationService.deleteDocumentLinkAsync({
          id: linkId.toString(),
          continue: false,
          configurationChangeId: this.cabinetConfigurationChangeId
        } as DeleteConfigurationModel);
        SnotifyConfiguration.showSuccess('Successfully deleted link');
        await this.getCabinetConfigurationAsync();
      } catch (error) {
        this.$log(error);
      } finally {
        this.deletingLinkId = null;
      }
    },
    setEditMode(this: any, linkId: number) {
      this.bidirectional = false;
      this.linkToEdit = {
        ...(this.cabinetConfiguration as CabinetConfigurationModel).documentLinks.find((x) => x.id === linkId)
      };
    },
    hasNoReferenceLinksConfigured(this: any) {
      const referenceLinks = this.referenceLinks as number[][];
      if (referenceLinks.every((x) => !x[0] || !x[1])) {
        this.validationError = 'At least one reference link must be configured';
        return true;
      }
      return false;
    },
    hasDuplicateReferenceLinks(this: any) {
      const referenceLinks = this.referenceLinks as number[][];
      if (referenceLinks.some((link) => referenceLinks.filter((otherLink) => otherLink[0] && otherLink[1] && otherLink[0] === link[0] && otherLink[1] === link[1]).length > 1)) {
        this.validationError = 'Cannot have duplicate reference links';
        return true;
      }
      return false;
    },
    isDuplicatedConfiguration(this: any): boolean {
      const documentLinks = (this.cabinetConfiguration as CabinetConfigurationModel)?.documentLinks;
      const linkToEdit = this.linkToEdit as DocumentLinkConfigurationModel;
      return !documentLinks.every((documentLink) => {
        if (
          linkToEdit[this.DOCUMENT_LINK_FIELDS.CabinetId] === documentLink[this.DOCUMENT_LINK_FIELDS.CabinetId] &&
          linkToEdit[this.DOCUMENT_LINK_FIELDS.SourceDocumentTypeId] === documentLink[this.DOCUMENT_LINK_FIELDS.SourceDocumentTypeId] &&
          linkToEdit[this.DOCUMENT_LINK_FIELDS.TargetDocumentTypeId] === documentLink[this.DOCUMENT_LINK_FIELDS.TargetDocumentTypeId]
        ) {
          const orderedDocumentReferenceLinks = this.getReferenceLinks(documentLink).sort();
          const orderedReferenceLinks = this.getReferenceLinks(linkToEdit).sort();
          if (this.isDuplicatedReferenceLink(orderedDocumentReferenceLinks, orderedReferenceLinks)) {
            this.validationError = 'Cannot have duplicate link configurations';
            return false;
          } else {
            return true;
          }
        }
        return true;
      });
    },
    isDuplicatedReferenceLink(orderedDocumentReferenceLinks: any, orderedReferenceLinks: any): boolean {
      return (
        orderedDocumentReferenceLinks.length === orderedReferenceLinks.length &&
        orderedDocumentReferenceLinks.every((link: any, i: number) => link[0] === orderedReferenceLinks[i][0] && link[1] === orderedReferenceLinks[i][1])
      );
    },
    getReferenceLinks(documentLinkConfigurationModel: DocumentLinkConfigurationModel) {
      return [
        [documentLinkConfigurationModel?.sourceReference1Id ?? 0, documentLinkConfigurationModel?.targetReference1Id ?? 0],
        [documentLinkConfigurationModel?.sourceReference2Id ?? 0, documentLinkConfigurationModel?.targetReference2Id ?? 0],
        [documentLinkConfigurationModel?.sourceReference3Id ?? 0, documentLinkConfigurationModel?.targetReference3Id ?? 0]
      ];
    },
    isBidirectionalAllowed(this: any) {
      if (
        this.bidirectional &&
        this.sourceDocumentTypeId === this.targetDocumentTypeId &&
        (this.referenceLinks as number[][]).filter((x) => Boolean(x[0]) && Boolean(x[1])).every((x) => x[0] === x[1])
      ) {
        this.validationError = 'Cannot have bidirectional links with all the same source and target references';
        return false;
      }
      return true;
    },
    validateMultiValueReferenceLinks(this: any) {
      let hasMultiValueReferences = false;
      const multiValueReferences: string[] = [];

      for (const [sourceReferenceId, targetReferenceId] of this.referenceLinks) {
        const sourceReference = this.sourceDocumentType.references.find((ref: CabinetReferenceModel) => ref.id === sourceReferenceId);

        const targetReference = this.targetDocumentType.references.find((ref: CabinetReferenceModel) => ref.id === targetReferenceId);

        if (sourceReference && sourceReference.isMultiValue === true) {
          multiValueReferences.push(sourceReference.referenceDescription);
          hasMultiValueReferences = true;
        }

        if (sourceReference && targetReference.isMultiValue === true) {
          multiValueReferences.push(targetReference.referenceDescription);
          hasMultiValueReferences = true;
        }
      }

      if (hasMultiValueReferences) {
        this.validationError = `Cannot create related document links on multi-value references. Multi-value references selected: ${multiValueReferences.join(', ')}`;
      }

      return hasMultiValueReferences;
    },
    async saveLinkConfiguration(this: any) {
      if (
        this.hasNoReferenceLinksConfigured() ||
        this.hasDuplicateReferenceLinks() ||
        this.isDuplicatedConfiguration() ||
        !this.isBidirectionalAllowed() ||
        this.validateMultiValueReferenceLinks()
      ) {
        return;
      }

      if (this.bidirectional) {
        const isBidirectionalLinkAvailable = await CabinetConfigurationService.checkExistingBidirectionalDocumentLink(this.linkToEdit);
        if (isBidirectionalLinkAvailable) {
          const confirmationMessage = 'You have requested a bi-directional link, but the reverse link already exists';
          const confirmationModalSettings: ConfirmationModalSettings = {
            title: 'Create new link and use existing reverse link?',
            okVariant: ColourVariantType.Primary,
            cancelVariant: ColourVariantType.Danger,
            cancelTitle: 'No'
          };
          const result: boolean = await this.showConfirmationModalAsync(this.$bvModal, confirmationMessage, confirmationModalSettings);
          if (!result) {
            return;
          }
          this.bidirectional = false;
        }
      }
      this.validationError = '';
      this.isSubmitting = true;
      const documentLinkConfiguration = {
        ...this.linkToEdit,
        configurationChangeId: this[StoreConstants.CabinetConfiguration.Getters.CabinetConfigurationChangeId]
      } as DocumentLinkConfigurationModel;
      try {
        if ((this.linkToEdit as DocumentLinkConfigurationModel).id) {
          this.cabinetConfiguration = await CabinetConfigurationService.updateDocumentLinkAsync(documentLinkConfiguration);
        } else {
          this.cabinetConfiguration = await CabinetConfigurationService.createDocumentLinkAsync(documentLinkConfiguration, this.bidirectional);
        }
        SnotifyConfiguration.showSuccess(`Successfully ${(this.linkToEdit as DocumentLinkConfigurationModel).id ? 'updated' : 'created'} link`);
        this.linkToEdit = {};
      } catch (error) {
        this.$log(error);
      } finally {
        this.isSubmitting = false;
      }
    },
    async cancelLinkConfigurationEdit(this: any) {
      const hasUnsavedChangesToKeep: boolean = await this.hasUnsavedChangesToKeepAsync(this.$bvModal, this.isApplicationDirty);
      if (hasUnsavedChangesToKeep) {
        return;
      }
      this.setApplicationDirty(false);
      this.linkToEdit = {};
      this.validationError = '';
    },
    getValidation(this: any, index: number) {
      return {
        notNullOrZero: Boolean(this.referenceLinks[index][0] || this.referenceLinks[index][1])
      } as ValidationModel;
    },
    async getAllCabinets(this: any) {
      try {
        const allCabinets = await CabinetConfigurationService.getAllCabinetsData();
        // Only keep data needed for page functionality for performance
        this.allCabinets = allCabinets.map(
          (cabinet) =>
            ({
              id: cabinet.id,
              description: cabinet.description,
              documentTypes: cabinet.documentTypes.map(
                (documentType) =>
                  ({
                    id: documentType.id,
                    description: documentType.description,
                    cabinetId: documentType.cabinetId,
                    references: documentType.references.map(
                      (reference) =>
                        ({
                          id: reference.id,
                          referenceDescription: reference.referenceDescription,
                          type: reference.type,
                          referenceNumber: reference.referenceNumber,
                          isMultiValue: reference.isMultiValue
                        } as CabinetReferenceModel)
                    )
                  } as DocumentTypeModel)
              )
            } as CabinetModel)
        );
      } catch (error) {
        this.$log(error);
      }
    },
    updateSourceReference(this: any, index: number, referenceId: number) {
      const updatedReference = referenceId || null;
      Vue.set(this.linkToEdit as DocumentLinkConfigurationModel, 'sourceReference' + index + 'Id', updatedReference);
    },
    updateTargetReference(this: any, index: number, referenceId: number) {
      const updatedReference = referenceId || null;
      Vue.set(this.linkToEdit as DocumentLinkConfigurationModel, 'targetReference' + index + 'Id', updatedReference);
    },
    addDocumentLink(this: any) {
      const defaultDocumentType = this.sourceDocumentTypes?.[0] as DocumentTypeModel;
      this.bidirectional = true;
      this.linkToEdit = {
        id: 0,
        cabinetId: this.$route.params.cabinetId,
        sourceDocumentTypeId: defaultDocumentType.id,
        targetDocumentTypeId: defaultDocumentType.id
      } as DocumentLinkConfigurationModel;
    }
  },
  async mounted(this: any) {
    await this.getLock();
    await Promise.all([this.getCabinetConfigurationAsync(), this.getAllCabinets()]);
    this.isLoading = false;
  },
  created(this: any) {
    this.$route.meta.treePath = `/configuration/cabinet/${this.$route.params.cabinetId}/dashboard`;
    this.TABLE_FIELDS = [{ key: 'actions', class: 'action-element' }, { key: 'linkDescription' }, { key: 'linkedRef1' }, { key: 'linkedRef2' }, { key: 'linkedRef3' }] as BvTableFieldArray;
    this.DOCUMENT_LINK_FIELDS = {
      CabinetId: 'cabinetId',
      SourceDocumentTypeId: 'sourceDocumentTypeId',
      TargetDocumentTypeId: 'targetDocumentTypeId'
    };
  },
  beforeDestroy(this: any) {
    this.$route.meta.treePath = null;
  },
  components: {
    MStoreFormSelect,
    MStoreTable,
    LoadingSpinner,
    MStoreFormCheckbox,
    MStoreSpinnerButton
  }
});
